import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook that automatically executes API call on mount/dependency change
 * Similar to RTK Query's automatic fetching behavior
 *
 * @param {Function} dispatchFn - dispatch function from useDispatch
 * @param {Function} apiFunc - Async thunk action creator
 * @param {Object} options - Options object
 * @param {boolean} options.skip - If true, skip the API call
 * @param {any} options.args - Arguments to pass to the API function
 * @param {Array} options.deps - Additional dependencies to trigger refetch
 * @returns {Object} - { isLoading, isSuccess, isError, error, refetch }
 */
export const useAutoFetchApi = (dispatchFn, apiFunc, options = {}) => {
  const { skip = false, args = null, deps = [] } = options;
  const [isLoading, setIsLoading] = useState(!skip);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (skip) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setError(null);
    setIsSuccess(false);

    try {
      const result = args !== null
        ? await dispatchFn(apiFunc(args))
        : await dispatchFn(apiFunc());

      if (!isMountedRef.current) return;

      if (result.isSuccess) {
        setIsSuccess(true);
      } else if (result.isError) {
        setIsError(true);
        setError(result.error);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setIsError(true);
      setError(err);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [dispatchFn, apiFunc, args, skip]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, skip, args, ...deps]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    isLoading,
    isSuccess,
    isError,
    error,
    refetch
  };
};

